/** @format */

import { memo } from 'react'

import styles from 'dir/styles/courses.module.css'
import flexDirectionAxisImg from '../../../assets/images/flex-direction-axis.jpg'

export const _1 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Технология Flexbox</h2>
    <p className={styles.description}>
      В настоящий момент наиболее популярной технологией позиционирования элементов является{' '}
      <code className={styles.code}>Flexbox</code>. У <code className={styles.code}>Flexbox</code> хорошая поддержка
      браузерами и на сегодняшний день это стандарт.{' '}
    </p>
    <p className={styles.description}>
      Идея технологии состоит в том, чтобы разделить элементы на блок-контейнер и управляемые элементы -
      непосредственные потомки контейнера. Блок-контейнер задает общий вид и направление группировки элементов
      (выравнивание в колонку или ряд, порядок появления элементов и расстояние между ними), а элементы имеют
      возможность задавать уникальные характеристики (встать на позицию другого элемента, изменив заданный в html
      порядок). Для осуществления такой гибкости в технологию введены новые абстракции - направления, вдоль которых
      выстраиваются элементы, линии границ блоков и элементов и т.п.{' '}
    </p>
    <iframe
      height="300"
      scrolling="no"
      title="flex-block-item"
      src="https://codepen.io/GoIT-fe/embed/yogBqa?default-tab=html%2Cresult"
      frameBorder="no"
      loading="lazy"
      allowTransparency="true"
      allowFullScreen="true"
    />
    <p className={styles.notificationWarn}>
      <b>Текст</b> как элемент <code className={styles.code}>flex-container</code>. Каждый дочерний элемент
      flex-контейнера становится flex-элементом. Текст, который напрямую содержится в flex-контейнере, оборачивается
      анонимным flex-элементом. Текст, состоящий из пробельных символов, ведет себя так, словно получает свойство{' '}
      <code className={styles.code}>display: none</code>.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.1 - Свойства flex-контейнера</h3>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>свойство</th>
            <th>значение</th>
            <th>поведение</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>display</td>
            <td>flex</td>
            <td>задает flex-контейнер c элементами block</td>
          </tr>
          <tr>
            <td>display</td>
            <td>inline-flex</td>
            <td>задает flex-контейнер c элементами inline-block</td>
          </tr>
        </tbody>
      </table>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.1.1 - Axis</h4>
        <p className={styles.description}>
          Элементы flex всегда располагаются вдоль главной оси. При этом главная ось может быть расположена
          горизонтально (row) или вертикально (column).{' '}
        </p>
        <ul className={styles.list}>
          <li className={styles.list__item}>Главная ось всегда перпендикулярна вспомогательной оси. </li>
          <li className={styles.list__item}>Направление главной оси можно изменить. </li>
          <li className={styles.list__item}>Порядок расположения элементов вдоль главной оси можно изменить. </li>
          <li className={styles.list__item}>Это не повлияет на направление вспомогательной оси.</li>
        </ul>
        <img className={styles.img} src={flexDirectionAxisImg} alt="flex-direction axis" />
        <p className={styles.description}>
          Пример ниже показывает сохранение направления вспомогательной оси при смене расположения элементов вдоль
          главной оси. Слово <code className={styles.code}>start</code> всегда прижато в этом примере к началу
          вспомогательной оси (top или left) вне зависимости от прямого или реверсного отображения элементов вдоль
          главной оси.{' '}
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="flex-axis-example"
          src="https://codepen.io/GoIT-fe/embed/xLXbma?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.1.2 - flex-direction</h4>
        <table className={styles.table}>
          <caption>Задает главную ось и порядок расположения элементов</caption>
          <thead>
            <tr>
              <th>свойство</th>
              <th>значение</th>
              <th>поведение</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>flex-direction</td>
              <td>row (default)</td>
              <td>задает горизонтальный порядок элементов слева направо</td>
            </tr>
            <tr>
              <td>flex-direction</td>
              <td>row-reverse</td>
              <td>задает горизонтальный порядок элементов справа налево</td>
            </tr>
            <tr>
              <td>flex-direction</td>
              <td>column</td>
              <td>задает вертикальный порядок элементов сверху вниз</td>
            </tr>
            <tr>
              <td>flex-direction</td>
              <td>column-reverse</td>
              <td>задает вертикальный порядок элементов снизу вверх</td>
            </tr>
          </tbody>
        </table>
        <iframe
          height="300"
          scrolling="no"
          title="flex-row-column"
          src="https://codepen.io/GoIT-fe/embed/vJgOYd?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.1.3 - flex-wrap</h4>
        <table className={styles.table}>
          <caption>Определяет возможность переноса и порядок расположения строк</caption>
          <thead>
            <tr>
              <th>свойство</th>
              <th>значение</th>
              <th>поведение</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>flex-wrap</td>
              <td>none (default)</td>
              <td>запрещает перенос элементов из основной оси</td>
            </tr>
            <tr>
              <td>flex-wrap</td>
              <td>wrap</td>
              <td>разрешает перенос элементов из основной оси на новую строку</td>
            </tr>
            <tr>
              <td>flex-wrap</td>
              <td>wrap-reverse</td>
              <td>
                разрешает перенос элементов из <b>основной оси</b> на новую строку (обратный порядок строк)
              </td>
            </tr>
          </tbody>
        </table>
        <iframe
          height="300"
          scrolling="no"
          title="flex-row-column"
          src="https://codepen.io/GoIT-fe/embed/eEgpPL?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
        <p className={styles.notificationWarn}>
          <code className={styles.code}>flex-flow</code> это краткая форма записи{' '}
          <code className={styles.code}>flex-direction</code> и <code className={styles.code}>flex-wrap</code>, например{' '}
          <code className={styles.code}>flex-flow: column wrap</code>{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.1.4 - justify-content</h4>
        <table className={styles.table}>
          <caption>
            Задает правило расположения элементов вдоль <b>основной оси</b> в текущем ряду.
          </caption>
          <thead>
            <tr>
              <th>свойство</th>
              <th>значение</th>
              <th>поведение</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>justify-content</td>
              <td>flex-start (default)</td>
              <td>элементы выравниваются от начала главной оси</td>
            </tr>
            <tr>
              <td>justify-content</td>
              <td>flex-end</td>
              <td>элементы выравниваются от конца главной оси</td>
            </tr>
            <tr>
              <td>justify-content</td>
              <td>center</td>
              <td>элементы выравниваются по центру главной оси </td>
            </tr>
            <tr>
              <td>justify-content</td>
              <td>space-between (default)</td>
              <td>
                элементы выравниваются по главной оси, расстояние между каждой парой смежных элементов одинаково. Края
                сливаются с границей первого и последнего элемента.
              </td>
            </tr>
            <tr>
              <td>justify-content</td>
              <td>space-around</td>
              <td>
                элементы выравниваются по главной оси, расстояние между каждой парой смежных элементов одинаково. Отступ
                по краям равен половине интервала между каждой парой смежных элементов.
              </td>
            </tr>
            <tr>
              <td>justify-content</td>
              <td>space-evenly</td>
              <td>
                элементы выравниваются по главной оси, c равным интервалом, расстояние между каждой парой смежных
                элементов и краями одинаково.{' '}
              </td>
            </tr>
          </tbody>
        </table>
        <iframe
          height="300"
          scrolling="no"
          title="flex-row-column"
          src="https://codepen.io/GoIT-fe/embed/KvaVaG?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.1.5 - Cross axis</h4>
        <p className={styles.description}>
          Дополнительная ось, всегда перпендикулярная основной оси. Особое поведение{' '}
          <code className={styles.code}>cross axis</code> состоит в том, что эта ось не меняет свое направление при
          смене направления главной оси.{' '}
        </p>
        <table className={styles.table}>
          <caption>
            Положение <b>элементов</b> вдоль <code className={styles.code}>cross axis</code> в одной строке.
          </caption>
          <thead>
            <tr>
              <th>свойство</th>
              <th>значение</th>
              <th>поведение</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>align-items</td>
              <td>stretch</td>
              <td>Значение по умолчанию. Элементы растягиваются так, чтобы занять всю высоту строки</td>
            </tr>
            <tr>
              <td>align-items</td>
              <td>flex-start</td>
              <td>элементы выравниваются от начала cross axis</td>
            </tr>
            <tr>
              <td>align-items</td>
              <td>flex-end</td>
              <td>элементы выравниваются от конца cross axis</td>
            </tr>
            <tr>
              <td>align-items</td>
              <td>center</td>
              <td>элементы выравниваются по центру cross axis</td>
            </tr>
            <tr>
              <td>align-items</td>
              <td>baseline</td>
              <td>элементы выравниваются по baseline текста, расположенного в элементах</td>
            </tr>
          </tbody>
        </table>
        <iframe
          height="300"
          scrolling="no"
          title="flex-row-column"
          src="https://codepen.io/GoIT-fe/embed/mMRVoX?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.1.6 - align-content</h4>
        <p className={styles.description}>
          Это свойство работает только если главная ось состоит из более чем одной строки. Другими словами, если
          <code className={styles.code}>flex-wrap: wrap</code> или{' '}
          <code className={styles.code}>flex-wrap: wrap-reverse</code> привели к переносу строк.{' '}
        </p>
        <table className={styles.table}>
          <caption>
            Положение <b>строк</b> вдоль <code className={styles.code}>cross axis</code> .
          </caption>
          <thead>
            <tr>
              <th>свойство</th>
              <th>значение</th>
              <th>поведение</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>align-content</td>
              <td>stretch</td>
              <td>Значение по умолчанию. Строки растягиваются так, чтобы занять всю площадь cross axis</td>
            </tr>
            <tr>
              <td>align-content</td>
              <td>flex-start</td>
              <td>строки выравниваются от начала cross axis</td>
            </tr>
            <tr>
              <td>align-content</td>
              <td>flex-end</td>
              <td>строки выравниваются от конца cross axis</td>
            </tr>
            <tr>
              <td>align-content</td>
              <td>center</td>
              <td>строки выравниваются по центру cross axis</td>
            </tr>
            <tr>
              <td>align-content</td>
              <td>space-between</td>
              <td>
                строки выравниваются вдоль cross axis так, чтобы крайние строки прижимались к краям, а свободное место
                распределялось равными частями между собой.
              </td>
            </tr>
            <tr>
              <td>align-content</td>
              <td>space-around</td>
              <td>
                строки выравниваются вдоль cross axis так, чтобы свободное место распределялось равными частями между
                собой, а крайние строки имели расстояние к краям равным половине от межстрочного расстояния.
              </td>
            </tr>
          </tbody>
        </table>
        <iframe
          height="300"
          scrolling="no"
          title="flex-row-column"
          src="https://codepen.io/GoIT-fe/embed/OjWNjq?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.2 - Свойства flex-элемента</h3>
      <p className={styles.description}>
        Мощь flex раскрывается в возможности управления положением отдельного элемента и даже изменение порядка
        расположения элемента без какого-либо вмешательства в код HTML.
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.2.1 - order</h4>
        <p className={styles.description}>
          Это возможность управления положением отдельного элемента. Элементы располагаются в порядке возрастания
          значения <code className={styles.code}>order</code>. При равенстве значения{' '}
          <code className={styles.code}>order</code> порядок задается последовательностью расположения элементов в HTML.
          По умолчанию у всех элементов <code className={styles.code}>order: 0</code>.{' '}
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="flex-row-column"
          src="https://codepen.io/GoIT-fe/embed/zdNwqK?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
        <p className={styles.notificationWarn}>
          Свойство <code className={styles.code}>order</code> влияет только на визуальное отображение. В не визуальных
          устройствах отображения, например системах озвучивающих содержимое сайта, этот свойство не работает.{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.2.2 - flex-grow</h4>
        <p className={styles.description}>
          Определят пропорцию значения <code className={styles.code}>width</code> элемента flex контейнера, которая
          будет рассчитана за счет удаления свободного пространства. По умолчанию это значение для всех элементов равно{' '}
          <code className={styles.code}>0</code>. Отрицательные значения не поддерживаются.{' '}
        </p>
        <p className={styles.description}>
          На рисунке ниже видно, что при значении 1 все элементы имеют равную ширину. А при равных пропорциях значения
          ширины элементов равны, хотя и имеют разное значение: <code className={styles.code}>flex-grow</code> - 2 для
          средней строки и 3 для нижней. Grow - это "рост", т.е. чем выше значение{' '}
          <code className={styles.code}>flex-grow</code> - тем больше ширина элемента.{' '}
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="flex-row-column"
          src="https://codepen.io/GoIT-fe/embed/NvdjZG?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.2.3 - flex-shrink</h4>
        <p className={styles.description}>
          Определят пропорцию значения <code className={styles.code}>width</code> элемента flex контейнера, которая
          будет рассчитана за счет ширины других элементов (т.е. при отсутствии свободного пространства). По умолчанию
          это значение для всех элементов равно 1. Отрицательные значения не поддерживаются.{' '}
        </p>
        <p className={styles.description}>
          На рисунке ниже видно что при значении 1 все элементы имеют равную ширину. А при равных пропорциях значения
          ширины элементов равны, хотя и имеют разное значение: <code className={styles.code}>flex-grow</code> - 2 для
          средней строки и 3 для нижней. Shrink - это "уменьшение", т.е. чем выше значение{' '}
          <code className={styles.code}>flex-shrink</code> - тем меньше ширина элемента.{' '}
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="flex-row-column"
          src="https://codepen.io/GoIT-fe/embed/brgRbV?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.2.4 - flex-basis</h4>
        <p className={styles.description}>
          Определяет ширину элемента <b>до</b> применения <code className={styles.code}>flex-grow</code> и{' '}
          <code className={styles.code}>flex-shrink</code>. Т.е. до распределения пространства flex-контейнера.
          Допустимы стандартные значения размеров в px, rem, % и т.п. Ожидается поддержка дополнительных значений этого
          свойства: content, max-content, min-content, fit-content и т.п.{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.2.5 - flex</h4>
        <p className={styles.description}>
          Краткая форма записи для перечисленных выше <code className={styles.code}>flex-grow</code>,{' '}
          <code className={styles.code}>flex-shrink</code> и <code className={styles.code}>flex-basis</code>.
          Обязательным для объявления является только <code className={styles.code}>flex-grow</code>.{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.2.6 - align-self</h4>
        <p className={styles.description}>
          Свойство, позволяющее изменить правило <code className={styles.code}>align-items</code> для отдельного
          элемента. К сожалению доступно всего два значения - <code className={styles.code}>flex-start</code> и{' '}
          <code className={styles.code}>flex-end</code>.{' '}
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="flex-row-column"
          src="https://codepen.io/GoIT-fe/embed/xLgrNN?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.3 - Дополнительные материалы</h3>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            target="_blank"
            rel="noopener noreferrer"
            href="http://css-live.ru/wp-content/uploads/2014/10/flexboxsheet-small__5.png"
          >
            Шпаргалка по Flexbox
          </a>
        </li>
        <li className={styles.list__item}>
          <a className={styles.link} target="_blank" rel="noopener noreferrer" href="https://html5.by/blog/flexbox/">
            Учим Flexbox
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            target="_blank"
            rel="noopener noreferrer"
            href="https://css-tricks.com/snippets/css/a-guide-to-flexbox/"
          >
            A Complete Guide to Flexbox от css-tricks
          </a>
        </li>
        <li className={styles.list__item}>
          <a className={styles.link} target="_blank" rel="noopener noreferrer" href="http://flexboxfroggy.com/#ru">
            Игра для изучения Flexbox
          </a>
        </li>
      </ul>
    </section>
  </section>
))
